
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { 
  BarChart3,
  Users,
  MapPin,
  Settings,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock,
  DollarSign,
  Briefcase,
  Bot,
  FileText,
  GraduationCap,
  Package,
  CheckSquare,
  MessageSquare,
  Activity,
  Zap
} from 'lucide-react';

const HQDashboard = () => {
  const systemStats = {
    totalStudios: 12,
    activeUsers: 1847,
    monthlyRevenue: 285000,
    pendingTasks: 5
  };

  const recentActivity = [
    { type: 'studio', message: 'Austin studio launched successfully', time: '2 hours ago', status: 'success' },
    { type: 'training', message: 'Houston staff completed Botox certification', time: '4 hours ago', status: 'success' },
    { type: 'revenue', message: 'Monthly revenue target exceeded by 15%', time: '1 day ago', status: 'success' },
    { type: 'system', message: 'AI agent Alma updated with new training data', time: '2 days ago', status: 'info' }
  ];

  const quickActions = [
    { title: 'Studio Management', icon: MapPin, href: '/hq/studios', color: 'bg-gradient-to-r from-amber-500 to-orange-600', description: 'Manage studio launches and operations' },
    { title: 'Staff & Training', icon: GraduationCap, href: '/hq/staff', color: 'bg-gradient-to-r from-blue-600 to-indigo-600', description: 'Track staff progress and training' },
    { title: 'Metrics & KPIs', icon: BarChart3, href: '/hq/metrics', color: 'bg-gradient-to-r from-emerald-600 to-teal-600', description: 'Revenue, bookings, and analytics' },
    { title: 'Task Management', icon: CheckSquare, href: '/hq/tasks', color: 'bg-gradient-to-r from-purple-600 to-pink-600', description: 'HQ action items and rollouts' },
    { title: 'Launch Checklists', icon: Briefcase, href: '/hq/checklists', color: 'bg-gradient-to-r from-red-600 to-rose-600', description: 'Site launch progress tracking' },
    { title: 'SOP Library', icon: FileText, href: '/hq/sops', color: 'bg-gradient-to-r from-slate-600 to-slate-800', description: 'Standard operating procedures' },
    { title: 'Launch Kits', icon: Package, href: '/hq/kits', color: 'bg-gradient-to-r from-cyan-600 to-blue-600', description: 'Download templates and materials' },
    { title: 'AI Agents', icon: Bot, href: '/hq/agents', color: 'bg-gradient-to-r from-violet-600 to-purple-600', description: 'Manage Miles, Alma, and Devon' },
    { title: 'Support Center', icon: MessageSquare, href: '/hq/support', color: 'bg-gradient-to-r from-orange-600 to-red-600', description: 'Internal support requests' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-amber-900">
      {/* Header */}
      <div className="bg-slate-800/50 backdrop-blur border-b border-amber-500/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-white">
                HQ Operations Center
              </h1>
              <p className="mt-1 text-amber-200">
                Central command for scaling, training, and managing all JUV operations
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <Badge className="bg-amber-500/20 text-amber-200 border-amber-500/30">
                <AlertCircle className="w-4 h-4 mr-1" />
                {systemStats.pendingTasks} Pending
              </Badge>
              <Badge className="bg-emerald-500/20 text-emerald-200 border-emerald-500/30">
                <CheckCircle className="w-4 h-4 mr-1" />
                All Systems Operational
              </Badge>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-8">
          {/* System Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-slate-800/50 backdrop-blur border-amber-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-amber-500/20">
                    <MapPin className="h-6 w-6 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm text-amber-200">Active Studios</p>
                    <p className="text-2xl font-bold text-white">
                      {systemStats.totalStudios}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 backdrop-blur border-emerald-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-emerald-500/20">
                    <Users className="h-6 w-6 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-sm text-emerald-200">Active Users</p>
                    <p className="text-2xl font-bold text-white">
                      {systemStats.activeUsers.toLocaleString()}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 backdrop-blur border-blue-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-blue-500/20">
                    <DollarSign className="h-6 w-6 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm text-blue-200">Monthly Revenue</p>
                    <p className="text-2xl font-bold text-white">
                      ${systemStats.monthlyRevenue.toLocaleString()}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-800/50 backdrop-blur border-purple-500/20">
              <CardContent className="p-6">
                <div className="flex items-center">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 bg-purple-500/20">
                    <TrendingUp className="h-6 w-6 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-sm text-purple-200">Growth Rate</p>
                    <p className="text-2xl font-bold text-white">
                      +24%
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions Grid - Updated with new structure */}
          <Card className="bg-slate-800/50 backdrop-blur border-slate-600/30">
            <CardHeader>
              <CardTitle className="text-white">Operations Hub</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {quickActions.map((action, index) => {
                  const Icon = action.icon;
                  return (
                    <Button
                      key={index}
                      asChild
                      className={`h-24 flex-col space-y-2 text-white hover:scale-105 transition-all duration-300 ${action.color}`}
                    >
                      <Link to={action.href}>
                        <Icon className="h-6 w-6" />
                        <div className="text-center">
                          <div className="text-sm font-medium">{action.title}</div>
                          <div className="text-xs opacity-90">{action.description}</div>
                        </div>
                      </Link>
                    </Button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Recent Activity */}
            <Card className="bg-slate-800/50 backdrop-blur border-slate-600/30">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Activity className="h-5 w-5 mr-2 text-amber-400" />
                  Recent Activity
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {recentActivity.map((activity, index) => (
                    <div key={index} className="flex items-start space-x-3 p-3 rounded-lg bg-slate-700/30">
                      <div className={`w-2 h-2 rounded-full mt-2 ${
                        activity.status === 'success' ? 'bg-emerald-400' :
                        activity.status === 'pending' ? 'bg-amber-400' :
                        'bg-blue-400'
                      }`}></div>
                      <div className="flex-1">
                        <p className="text-white text-sm">{activity.message}</p>
                        <p className="text-slate-400 text-xs mt-1">{activity.time}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* System Health */}
            <Card className="bg-slate-800/50 backdrop-blur border-slate-600/30">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Zap className="h-5 w-5 mr-2 text-emerald-400" />
                  System Health
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-emerald-400" />
                      <span className="text-white">All Studios Online</span>
                    </div>
                    <span className="text-emerald-400 text-sm">100%</span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-emerald-400" />
                      <span className="text-white">AI Agents Active</span>
                    </div>
                    <span className="text-emerald-400 text-sm">3/3</span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
                    <div className="flex items-center space-x-3">
                      <Clock className="w-5 h-5 text-amber-400" />
                      <span className="text-white">Database Optimization</span>
                    </div>
                    <span className="text-amber-200 text-sm">In Progress</span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/20">
                    <div className="flex items-center space-x-3">
                      <CheckCircle className="w-5 h-5 text-emerald-400" />
                      <span className="text-white">Data Backup</span>
                    </div>
                    <span className="text-emerald-400 text-sm">Up to date</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HQDashboard;
